import { resolve, reject } from 'redux-optimist-promise';
import { get, find } from 'lodash';
import { getPriceDetailFragment } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { getDefaultProposal } from 'Utils/proposal/proposalUtils';
import logger from 'Logger';
import { getBookingAccommodationsSearch } from 'Components/utils/booking/bookingAccommodationsMore.queries';
import {
  getAdditionalAccommodations,
  getAdditionalAccommodationsFilters,
  getAdditionalAccommodationsPmrChecked,
  getProposals,
  getSelectedProposalId,
  getCurrentResort,
} from 'Store/booking/booking.selector';
import createAction from 'Store/utils/createAction';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  CUSTOMER_BOOKING_GET_MORE_ACCOMMODATIONS_DATA,
  PROPOSAL_GET_ADDITIONAL_ACCOMMODATIONS,
} from 'Utils/request/requestDictionary';
const CLEAR_ADDITIONAL_ACCOMMODATIONS = 'CLEAR_ADDITIONAL_ACCOMMODATIONS';
const GET_ALTERNATIVES_ACCOMMODATIONS_BY_COMFORT = 'GET_ALTERNATIVES_ACCOMMODATIONS_BY_COMFORT';
const FETCH_ADDITIONAL_ACCOMMODATIONS = 'FETCH_ADDITIONAL_ACCOMMODATIONS';
const CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS = 'CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS';
const UPDATE_ADDITIONAL_ACCOMMODATIONS_FILTERS = 'UPDATE_ADDITIONAL_ACCOMMODATIONS_FILTERS';
const SET_FILTERED_ADDITIONAL_ACCOMMODATIONS = 'SET_FILTERED_ADDITIONAL_ACCOMMODATIONS';
const TOGGLE_ACCOMMODATIONS_FILTER = 'TOGGLE_ACCOMMODATIONS_FILTER';
const UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY =
  'UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY';
const UPDATE_FILTERED_ACCOMMODATIONS_RESULTS = 'UPDATE_FILTERED_ACCOMMODATIONS_RESULTS';
const TOGGLE_EXTRA_FEATURES = 'TOGGLE_EXTRA_FEATURES';
const TOGGLE_PMR_CHECKBOX = 'TOGGLE_PMR_CHECKBOX';

function _getSelectedProposal(state: ReduxState): Proposal | null | undefined {
  if (!state) {
    return null;
  }

  const proposals = getProposals(state) || [];
  const resort = getCurrentResort(state);
  const selectedProposal: Proposal | null | undefined =
    find(proposals, {
      id: getSelectedProposalId(state),
    }) || getDefaultProposal(proposals, resort);
  return selectedProposal;
}

type AdditionalAccommodationsFiltersProps = {
  getState: GetReduxState;
  i18n: CMSContent;
  product: Product;
  proposal: Proposal;
  shouldPreselectFilters: boolean;
};

function _createAdditionalAccommodationsFilters({
  getState,
  i18n,
  product,
  proposal,
  shouldPreselectFilters,
}: AdditionalAccommodationsFiltersProps) {
  return createAction(CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    i18n,
    product,
    reducedMobilityVisibility: getAdditionalAccommodationsPmrChecked(getState()),
    selectedProposal: proposal,
    shouldPreselectFilters,
  });
}

function _updateAdditionalAccommodationsFilters(isPmrChecked: boolean, getState: GetReduxState) {
  return createAction(UPDATE_ADDITIONAL_ACCOMMODATIONS_FILTERS, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    selectedProposal: _getSelectedProposal(getState()),
    filters: getAdditionalAccommodationsFilters(getState()),
    reducedMobilityVisibility: isPmrChecked ?? getAdditionalAccommodationsPmrChecked(getState()),
  });
}

function _toggleFilter(filter: Filter, getState: GetReduxState) {
  return createAction(TOGGLE_ACCOMMODATIONS_FILTER, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    selectedProposal: _getSelectedProposal(getState()),
    reducedMobilityVisibility: getAdditionalAccommodationsPmrChecked(getState()),
    filter,
  });
}

function _unselectFiltersCategory(category: string, getState: GetReduxState) {
  return createAction(UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    selectedProposal: _getSelectedProposal(getState()),
    reducedMobilityVisibility: getAdditionalAccommodationsPmrChecked(getState()),
    category,
  });
}

function _updateFilterAccommodations(getState: GetReduxState) {
  return createAction(UPDATE_FILTERED_ACCOMMODATIONS_RESULTS, {
    additionalAccommodations: getAdditionalAccommodations(getState()),
    selectedProposal: _getSelectedProposal(getState()),
    filters: getAdditionalAccommodationsFilters(getState()),
    reducedMobilityVisibility: getAdditionalAccommodationsPmrChecked(getState()),
  });
}

type additionalAccommodationsQueryArgs = {
  proposalId: string;
  appContext: AppContext;
  duration: number;
  selectedRate?: string;
  roomsArrangement?: Array<any>;
};

function _runAdditionalAccommodationsQuery({
  proposalId,
  appContext,
  duration,
  selectedRate = '',
}: additionalAccommodationsQueryArgs): Promise<any> {
  const query = `
    {
      additionalAccommodations(proposalId: "${proposalId}") {
        id
        isUpgradableOnline
        totalDifferentialPrice(selectedRate: "${selectedRate}") {
          ...priceDetails
        }
        differentialPriceByNight(nightsNumber: ${duration}, selectedRate: "${selectedRate}") {
          formattedValue(precision: 0)
          value
        }
        remainingStock
        accommodations {
          id
          categories { id label }
          category
          categoryId
          comfortType
          exclusiveCollection
          connectingRoom
          floorPlans
          description
          label
          maxCapacity
          roomType
          media {
            photos {
              url
            }
          }
          minCapacity
          occupation
          quantity
          occupancies {
            baby_count
          }
          outsideFacilities {
            id
            label
          }
          bathroomEquipments {
            id
            label
          }
          equipments {
            id
            label
          }
          services { id label }
          strengths { id label }
          surface
          view { id label }
          reducedMobilityAccess
          attendeesDistribution {
            id
            quantity
            attendees {
              id
              type
              birthdate
              customerId
            }
          }
        }
      }
      config {
        filters {
          configuration {
            categoryTitle
            simpleRoom
            simpleRooms
            separatedRooms
            separatedRooms3
          }
          comfort {
            categoryTitle
          }
          extraFeatures {
            categoryTitle
          }
          reducedMobility
        }
      }
    }
    ${getPriceDetailFragment(get(appContext, 'locale'))}
  `;
  return graphQLClient.runNoCacheQuery(query, appContext, {
    requestName: PROPOSAL_GET_ADDITIONAL_ACCOMMODATIONS,
  });
}

function clearAdditionalAccommodations() {
  return createAction(CLEAR_ADDITIONAL_ACCOMMODATIONS);
}

export type FetchAdditionalAccommodationsArgs = {
  appContext: AppContext;
  product: Product;
  proposal: Proposal;
  roomsArrangement?: Array<{
    attendees: Array<{
      id: string;
    }>;
    categories: Array<string>;
  }>;
  shouldPreselectFilters?: boolean;
};

function getAvailableFilteredAccommodationsByComfort({
  additionalAccommodations,
}: {
  additionalAccommodations: Array<any>;
}) {
  return createAction(GET_ALTERNATIVES_ACCOMMODATIONS_BY_COMFORT, {
    additionalAccommodations: additionalAccommodations,
  });
}

export type GetAlternativeFilteredAccommodationsArgs = {
  proposal: Proposal;
  appContext: AppContext;
};

function getAlternativeFilteredAccommodations({
  proposal,
  appContext,
}: GetAlternativeFilteredAccommodationsArgs) {
  const {
    id,
    duration,
    price: { selectedRate },
  } = proposal;
  return (dispatch: ReduxDispatch) => {
    dispatch(createAction(FETCH_ADDITIONAL_ACCOMMODATIONS));
    return _runAdditionalAccommodationsQuery({
      proposalId: id,
      appContext,
      duration,
      selectedRate,
    })
      .then(({ additionalAccommodations }) => {
        dispatch(
          createAction(resolve(FETCH_ADDITIONAL_ACCOMMODATIONS), {
            additionalAccommodations,
          }),
        );
        dispatch(
          getAvailableFilteredAccommodationsByComfort({
            additionalAccommodations: additionalAccommodations,
          }),
        );
        dispatch(createAction(resolve(GET_ALTERNATIVES_ACCOMMODATIONS_BY_COMFORT)));
      })
      .catch((error) => {
        logger.error('error', error);
      });
  };
}

function fetchAdditionalAccommodations({
  appContext,
  product,
  proposal,
  roomsArrangement = [],
  shouldPreselectFilters = true,
}: FetchAdditionalAccommodationsArgs) {
  const {
    id,
    duration,
    price: { selectedRate },
  } = proposal;
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(createAction(FETCH_ADDITIONAL_ACCOMMODATIONS));
    return _runAdditionalAccommodationsQuery({
      proposalId: id,
      appContext,
      duration,
      selectedRate,
      roomsArrangement,
    })
      .then(({ additionalAccommodations, config: i18n }) => {
        dispatch(
          createAction(resolve(FETCH_ADDITIONAL_ACCOMMODATIONS), {
            additionalAccommodations,
          }),
        );
        dispatch(
          _createAdditionalAccommodationsFilters({
            getState,
            i18n,
            product,
            proposal,
            shouldPreselectFilters,
          }),
        );
        dispatch(_updateFilterAccommodations(getState));
      })
      .catch((error) => {
        dispatch(createAction(reject(FETCH_ADDITIONAL_ACCOMMODATIONS), error));
      });
  };
}

function toggleAdditionalAccommodationFilter(filter: Filter) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(_toggleFilter(filter, getState));
    dispatch(_updateFilterAccommodations(getState));
  };
}

function unselectAdditionalAccommodationsFilterCategory(category: string) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(_unselectFiltersCategory(category, getState));
    dispatch(_updateFilterAccommodations(getState));
  };
}

function togglePMRChecbox(isChecked: boolean, filterValues: any) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(
      createAction(TOGGLE_PMR_CHECKBOX, {
        isChecked,
        filterValues,
      }),
    );
    dispatch(_updateAdditionalAccommodationsFilters(isChecked, getState));
  };
}

function toggleAdditionalAccommodationReducedMobilityFilter(
  accommodationsSets: Array<any>,
  visibility: boolean,
) {
  return (dispatch: ReduxDispatch) => {
    createAction(resolve(FETCH_ADDITIONAL_ACCOMMODATIONS), {
      additionalAccommodations: accommodationsSets,
    });
    dispatch(
      createAction(TOGGLE_EXTRA_FEATURES, {
        accommodationsSets,
        reducedMobilityVisibility: visibility,
      }),
    );
  };
}

type additionalBookingAccommodationsQueryArgs = {
  appContext: AppContext;
  attendees: Array<LightAttendee>;
  bookingId: string;
  duration: number;
  selectedRate?: string;
};

const _runMoreAccommodationsQuery = ({
  appContext,
  attendees = [],
  bookingId,
  duration,
  selectedRate = '',
}: additionalBookingAccommodationsQueryArgs): Promise<any> => {
  const query = getBookingAccommodationsSearch(appContext, bookingId, {
    attendees,
    duration,
    selectedRate,
  });
  return graphQLClient.runNoCacheQuery(query, appContext, {
    requestName: CUSTOMER_BOOKING_GET_MORE_ACCOMMODATIONS_DATA,
    cacheFront: {
      key: `${CUSTOMER_BOOKING_GET_MORE_ACCOMMODATIONS_DATA}_${bookingId}`,
    },
  });
};

const isNotSelected = (set, selectedAccommodation): boolean =>
  set.accommodations[0].id !== selectedAccommodation.id &&
  set.totalDifferentialPrice.value !== selectedAccommodation.differentialPrice.amount;

type FetchBookingAdditionnalAccommodationsArgs = {
  appContext: AppContext;
  attendees: Array<LightAttendee>;
  bookingId: string;
  duration: number;
  ignoreReducedMobility?: boolean;
  product: Product;
  selectedAccommodation: Accommodation;
  selectedRate?: string;
  shouldPreselectFilters?: boolean;
};
export const fetchBookingMoreAccommodations = (args: FetchBookingAdditionnalAccommodationsArgs) => {
  const {
    appContext,
    attendees,
    bookingId,
    duration,
    ignoreReducedMobility = false,
    product,
    selectedAccommodation = {},
    selectedRate = '',
    shouldPreselectFilters = false,
  } = args;
  return async (dispatch: ReduxDispatch, getState: GetReduxState) => {
    dispatch(createAction(FETCH_ADDITIONAL_ACCOMMODATIONS));

    try {
      const { bookingAdditionalAccommodations } = await _runMoreAccommodationsQuery({
        appContext,
        attendees,
        bookingId,
        duration,
        selectedRate,
      });
      dispatch(
        createAction(resolve(FETCH_ADDITIONAL_ACCOMMODATIONS), {
          additionalAccommodations: bookingAdditionalAccommodations.filter((set) => {
            return (
              set.remainingStock > 0 &&
              set.totalDifferentialPrice.value > 0 && // only more expensive accommodation
              Boolean(set.accommodations.length) &&
              isNotSelected(set, selectedAccommodation) &&
              (ignoreReducedMobility
                ? !set.accommodations.some(({ reducedMobilityAccess }) =>
                    Boolean(reducedMobilityAccess),
                  )
                : true)
            );
          }),
        }),
      );
      dispatch(
        _createAdditionalAccommodationsFilters({
          getState,
          i18n: {},
          product,
          proposal: {},
          shouldPreselectFilters,
        }),
      );
      dispatch(_updateFilterAccommodations(getState));
    } catch (error) {
      dispatch(createAction(reject(FETCH_ADDITIONAL_ACCOMMODATIONS), error));
    }
  };
};
export {
  CLEAR_ADDITIONAL_ACCOMMODATIONS,
  FETCH_ADDITIONAL_ACCOMMODATIONS,
  SET_FILTERED_ADDITIONAL_ACCOMMODATIONS,
  GET_ALTERNATIVES_ACCOMMODATIONS_BY_COMFORT,
  TOGGLE_ACCOMMODATIONS_FILTER,
  UNSELECT_ADDITIONAL_ACCOMMODATIONS_FILTER_CATEGORY,
  CREATE_ADDITIONAL_ACCOMMODATIONS_FILTERS,
  UPDATE_ADDITIONAL_ACCOMMODATIONS_FILTERS,
  UPDATE_FILTERED_ACCOMMODATIONS_RESULTS,
  TOGGLE_EXTRA_FEATURES,
  TOGGLE_PMR_CHECKBOX,
  clearAdditionalAccommodations,
  fetchAdditionalAccommodations,
  getAlternativeFilteredAccommodations,
  toggleAdditionalAccommodationFilter,
  unselectAdditionalAccommodationsFilterCategory,
  toggleAdditionalAccommodationReducedMobilityFilter,
  togglePMRChecbox,
};
